import { motion } from 'framer-motion'
import { AlertTriangle, ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { cn } from '@/lib/utils'
import { useCommandDashboard } from '@/contexts/CommandDashboardContext'
import { resolveCommandActionRoute } from '@/lib/commandRoutes'

const LEVEL_EMOJI = { urgent: '🔴', important: '🟠', warning: '🟡', info: '🔵' }

const ITEMS = [
  {
    level: 'urgent',
    emoji: '🔴',
    title: 'VIP arrival in 15 minutes',
    detail: 'Noor Al-Said · Bridal Glow · prep Bridal Lounge + welcome drink',
    action: 'Prepare suite',
  },
  {
    level: 'urgent',
    emoji: '🔴',
    title: 'Pending payment · AED 850',
    detail: 'Sara Khalifa · balance after Royal Hydra Facial',
    action: 'Collect now',
  },
  {
    level: 'important',
    emoji: '🟠',
    title: 'Cancellation request',
    detail: 'Amira T. · 4:00 PM massage · requested via WhatsApp',
    action: 'Review',
  },
  {
    level: 'warning',
    emoji: '🟡',
    title: 'Possible no-show',
    detail: 'Huda M. · 1:30 PM · no confirmation reply (2 reminders sent)',
    action: 'Call guest',
  },
  {
    level: 'info',
    emoji: '🔵',
    title: 'Inventory reminder',
    detail: 'La Mer treatment serum · 2 units · reorder before weekend',
    action: 'View stock',
  },
]

const LEVEL = {
  urgent: {
    card: 'bg-gradient-to-r from-red-50/80 to-card shadow-sm',
    accent: 'border-l-[3px] border-l-red-500',
    title: 'text-red-900',
  },
  important: {
    card: 'bg-gradient-to-r from-orange-50/60 to-card',
    accent: 'border-l-[3px] border-l-orange-400',
    title: 'text-orange-900',
  },
  warning: {
    card: 'bg-gradient-to-r from-amber-50/50 to-card',
    accent: 'border-l-[3px] border-l-amber-400',
    title: 'text-amber-900',
  },
  info: {
    card: 'bg-gradient-to-r from-sky-50/40 to-card',
    accent: 'border-l-[3px] border-l-sky-400',
    title: 'text-foreground',
  },
}

export function AttentionCenter() {
  const navigate = useNavigate()
  const { data, dismissAttention } = useCommandDashboard()
  const items = Array.isArray(data?.attention)
    ? data.attention.map((item) => ({
        ...item,
        emoji: LEVEL_EMOJI[item.level] ?? '🔵',
        action: item.action_label ?? item.action,
      }))
    : []
  const urgentCount = items.filter((i) => i.level === 'urgent').length

  async function handleItemClick(item) {
    if (item.id && item.action_type) {
      try {
        await dismissAttention(item.id)
      } catch {
        // Still navigate even if snooze fails.
      }
    }

    const route = resolveCommandActionRoute(item.action_type, item.entity_id)
    navigate(route)
  }

  return (
    <section className="card-attention p-4 lg:p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-500/10">
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </div>
          <div>
            <h2 className="text-sm font-bold tracking-tight text-foreground">Attention center</h2>
            <p className="text-[11px] text-muted-foreground">Act before guests arrive</p>
          </div>
        </div>
        <span className="rounded-full bg-red-500/10 px-2.5 py-1 text-[10px] font-bold text-red-600">
          {urgentCount} urgent
        </span>
      </div>

      <div className="space-y-1.5">
        {items.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">All clear — nothing needs attention.</p>
        ) : null}
        {items.map((item, i) => {
          const lv = LEVEL[item.level]
          return (
            <motion.button
              key={item.id ?? item.title}
              type="button"
              onClick={() => handleItemClick(item)}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + i * 0.04 }}
              whileHover={{ scale: 1.005 }}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-xl p-2.5 text-left transition-shadow hover:shadow-luxury-sm',
                lv.card,
                lv.accent,
                item.level === 'urgent' && 'shadow-sm',
              )}
              style={{ border: '1px solid hsl(262 20% 50% / 0.03)' }}
            >
              <span className="text-base leading-none">{item.emoji}</span>
              <div className="min-w-0 flex-1">
                <p className={cn('text-[12px] font-bold leading-tight', lv.title)}>{item.title}</p>
                <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
                  {item.detail}
                </p>
              </div>
              <span className="hidden shrink-0 items-center gap-0.5 text-[11px] font-semibold text-primary sm:flex">
                {item.action}
                <ChevronRight className="h-3.5 w-3.5" />
              </span>
            </motion.button>
          )
        })}
      </div>
    </section>
  )
}
